import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Trans } from '@lingui/macro';

import { getTextColorClass, getColor } from '@utils/Utils';
import { BasicData } from '@App';

import './style.scss';

const Tabs = ({ active = true, children, onTabClick }) => {
    const { theme } = useContext(BasicData);
    const colors = getColor(theme);

    const tabsRef = useRef(null);
    const [showNavigation, setShowNavigation] = useState(false);

    const childrenArr = useMemo(() => (
        React.Children.toArray(children) || []
    ), [children])

    const [activeTab, setActiveTab] = useState(0);

    const handleTabClick = useCallback((e) => {
            const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);
            setActiveTab(index);
            if (onTabClick) {
                onTabClick(index, e);
            }
        },
        [onTabClick]
    );

    const prevTab = useCallback(() => {
        setActiveTab((prev) =>
            prev === 0 ? childrenArr.length - 1 : prev - 1
        );
    }, [childrenArr]);

    const nextTab = useCallback(() => {
        setActiveTab((prev) =>
            prev === childrenArr.length - 1 ? 0 : prev + 1
        );
    }, [childrenArr]);

    useEffect(() => {
        const checkNavigation = () => {
            if (tabsRef.current) {
                const tabsWidth = tabsRef.current.scrollWidth;
                const sectionWidth = window.innerWidth * 0.67;
                setShowNavigation(tabsWidth + 100 > sectionWidth);
            }
        };
        checkNavigation();
        window.addEventListener('resize', checkNavigation);
        return () => window.removeEventListener('resize', checkNavigation);
    }, []);

    useEffect(() => {
        setActiveTab(0);
    }, [active]);

    useEffect(() => {
        if (tabsRef.current && tabsRef.current.children[activeTab]) {
            const activeElement = tabsRef.current.children[activeTab];
            const parentRect = tabsRef.current.getBoundingClientRect();
            const activeRect = activeElement.getBoundingClientRect();
            if (activeRect.right > parentRect.right) {
                tabsRef.current.scrollLeft += activeRect.right - parentRect.right;
            } else if (activeRect.left < parentRect.left) {
                tabsRef.current.scrollLeft -= parentRect.left - activeRect.left;
            }
        }
    }, [activeTab]);

    const activeChild = useMemo(() => {
        const child = childrenArr[activeTab];
        return React.isValidElement(child)
            ? React.cloneElement(child, { active: true })
            : child;
    }, [activeTab, childrenArr]);

    return (
        <div className={`tabs ${active ? 'active' : ''}`}>
            <div className="tabs-navigation">
                {showNavigation && (
                    <i className="icon-chevron-left" onClick={prevTab} />
                )}
                <ul ref={tabsRef}>
                    {childrenArr.map((child, index) => {
                        if (!child.props?.title) return null;
                        const isActive = activeTab === index;
                        const style = {
                            backgroundColor: isActive
                                ? colors?.accent
                                    ? `#${colors.accent}`
                                    : '#534D67'
                                : 'transparent',
                        };
                        return (
                            <li
                                key={index}
                                data-index={index}
                                className={`${isActive ? 'active' : ''} ${getTextColorClass(
                                    colors?.accent
                                )}`}
                                onClick={handleTabClick}
                                style={style}
                            >
                                <h6>
                                    <Trans id={child.props.title} values={child.props.title} />
                                </h6>
                            </li>
                        );
                    })}
                </ul>
                {showNavigation && (
                    <i className="icon-chevron-right" onClick={nextTab} />
                )}
            </div>
            <div className="tab-content">{activeChild}</div>
        </div>
    );
};

const Tab = ({ active, children }) => (
    <div className={`tab ${active ? 'active' : ''}`}>{children}</div>
);

export { Tabs, Tab };
